import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import PlanSelection from './PlanSelection';
import PhoneNumberModule from '../modules/phone-number/PhoneNumberModule.jsx';
import { ModuleContextProvider } from '../modules/context/ModuleContextProvider.jsx';
import ParrotCreation from './ParrotCreation';
import LoadingSpinner from './LoadingSpinner';
import OnboardingErrorDisplay from './OnboardingErrorDisplay';
import OnboardingProgressIndicator, { OnboardingProgressBar } from './OnboardingProgressIndicator';
import OnboardingSuccessDisplay, { StepCompletionCelebration, OnboardingSuccessToast } from './OnboardingSuccessDisplay';
import OnboardingFeedback, { QuickFeedback } from './OnboardingFeedback';
import { HelpTooltip } from './OnboardingTooltip';
import { 
  createOnboardingErrorHandler, 
  parseApiError, 
  OnboardingError, 
  ERROR_TYPES, 
  ERROR_SEVERITY,
  checkNetworkConnectivity,
  trackOnboardingError
} from '../utils/onboarding-errors';

const ONBOARDING_STEPS = [
  { id: 1, title: 'Select Your Plan', description: 'Choose the perfect plan for your needs', component: 'plan' },
  { id: 2, title: 'Choose Phone Number', description: 'Get phone numbers for your Parrot (optional - you can add more later)', component: 'phone' },
  { id: 3, title: 'Create Your Parrot', description: 'Set up your AI assistant', component: 'parrot' }
];

export default function EnhancedOnboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showStepCelebration, setShowStepCelebration] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const navigate = useNavigate();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  
  // Onboarding session data
  const [sessionData, setSessionData] = useState(null);
  const [onboardingData, setOnboardingData] = useState({
    selectedPlan: null,
    phoneNumber: null,
    parrotConfig: null
  });

  // Create error handler
  const errorHandler = createOnboardingErrorHandler(setError, setIsLoading);

  // Initialize onboarding session
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      initializeOnboarding();
    } else if (isLoaded && !isSignedIn) {
      navigate('/sign-in');
    }
  }, [isLoaded, isSignedIn, navigate]);

  const initializeOnboarding = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check network connectivity first
      const isConnected = await checkNetworkConnectivity();
      if (!isConnected) {
        throw new OnboardingError(
          'No internet connection detected. Please check your connection and try again.',
          ERROR_TYPES.NETWORK,
          ERROR_SEVERITY.HIGH,
          { retryable: true }
        );
      }

      const token = await getToken();
      
      const response = await fetch('https://parrotmate.com/api/onboarding/start', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(30000) // 30 second timeout
      });

      if (!response.ok) {
        const onboardingError = await parseApiError(response);
        throw onboardingError;
      }

      const data = await response.json();
      
      if (data.redirect_to_dashboard) {
        // User already completed onboarding
        navigate('/dashboard?onboarding=complete');
        return;
      }

      setSessionData(data.session);
      setCurrentStep(data.next_step || 1);
      setRetryCount(0); // Reset retry count on success
      
    } catch (error) {
      console.error('Failed to initialize onboarding:', error);
      
      // Handle different error types
      if (error.name === 'AbortError') {
        const timeoutError = new OnboardingError(
          'Request timed out. Please check your connection and try again.',
          ERROR_TYPES.TIMEOUT,
          ERROR_SEVERITY.MEDIUM,
          { retryable: true }
        );
        errorHandler.handleError(timeoutError, { operation: 'initialize_onboarding' });
      } else {
        errorHandler.handleError(error, { operation: 'initialize_onboarding' });
      }
      
      // Track error for monitoring
      trackOnboardingError(error, { 
        operation: 'initialize_onboarding',
        retry_count: retryCount 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOnboardingProgress = async () => {
    try {
      const token = await getToken();
      
      const response = await fetch('https://parrotmate.com/api/onboarding/progress', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(15000) // 15 second timeout
      });

      if (!response.ok) {
        const onboardingError = await parseApiError(response);
        throw onboardingError;
      }

      const data = await response.json();
      
      if (data.is_complete) {
        navigate('/dashboard?onboarding=complete');
        return;
      }

      // Update current step based on progress
      if (data.next_step) {
        setCurrentStep(data.next_step);
      }

      // Update onboarding data based on actual status
      setOnboardingData(prev => ({
        ...prev,
        selectedPlan: data.context.selected_plan || prev.selectedPlan,
        phoneNumber: data.context.phone_numbers_count > 0 ? { selected: true } : prev.phoneNumber,
        parrotConfig: data.context.parrots_count > 0 ? { created: true } : prev.parrotConfig
      }));

      return data;
      
    } catch (error) {
      console.error('Failed to fetch onboarding progress:', error);
      
      if (error.name === 'AbortError') {
        const timeoutError = new OnboardingError(
          'Request timed out while loading progress. Please try again.',
          ERROR_TYPES.TIMEOUT,
          ERROR_SEVERITY.MEDIUM,
          { retryable: true }
        );
        errorHandler.handleError(timeoutError, { operation: 'fetch_progress' });
      } else {
        errorHandler.handleError(error, { operation: 'fetch_progress' });
      }
      
      trackOnboardingError(error, { operation: 'fetch_progress' });
    }
  };

  const handleNext = async () => {
    // Validate current step
    if (!canProceed()) {
      return;
    }

    if (currentStep < ONBOARDING_STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete onboarding
      await completeOnboarding();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeOnboarding = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = await getToken();
      
      const response = await fetch('https://parrotmate.com/api/onboarding/complete', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(30000) // 30 second timeout
      });

      if (!response.ok) {
        const onboardingError = await parseApiError(response);
        throw onboardingError;
      }

      const data = await response.json();
      
      if (data.success) {
        // Track successful completion
        trackOnboardingError(
          new OnboardingError('Onboarding completed successfully', ERROR_TYPES.UNKNOWN, ERROR_SEVERITY.LOW),
          { operation: 'complete_onboarding', success: true }
        );
        
        navigate('/dashboard?onboarding=complete');
      }
      
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      
      if (error.name === 'AbortError') {
        const timeoutError = new OnboardingError(
          'Request timed out while completing onboarding. Please try again.',
          ERROR_TYPES.TIMEOUT,
          ERROR_SEVERITY.MEDIUM,
          { retryable: true }
        );
        errorHandler.handleError(timeoutError, { operation: 'complete_onboarding' });
      } else {
        errorHandler.handleError(error, { operation: 'complete_onboarding' });
      }
      
      trackOnboardingError(error, { operation: 'complete_onboarding' });
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!onboardingData.selectedPlan;
      case 2: return true; // Phone number is optional
      case 3: return !!onboardingData.parrotConfig;
      default: return true;
    }
  };

  const handleStepComplete = (stepType, data) => {
    setOnboardingData(prev => ({
      ...prev,
      [stepType]: data
    }));
    
    // Mark step as completed
    setCompletedSteps(prev => [...prev, currentStep]);
    
    // Show success feedback
    const stepName = ONBOARDING_STEPS[currentStep - 1].title;
    setSuccessToastMessage(`${stepName} completed successfully!`);
    setShowSuccessToast(true);
    
    // Show step celebration for important steps
    if (currentStep === 1 || currentStep === 3) {
      setShowStepCelebration(true);
    } else {
      // Auto-advance for less critical steps
      if (currentStep < ONBOARDING_STEPS.length) {
        setTimeout(() => setCurrentStep(currentStep + 1), 1000);
      }
    }
    
    // Show feedback collection after step completion
    setTimeout(() => setShowFeedback(true), 2000);
  };

  const handleCelebrationComplete = () => {
    setShowStepCelebration(false);
    if (currentStep < ONBOARDING_STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleFeedbackSubmit = (feedbackData) => {
    console.log('Feedback submitted:', feedbackData);
    setShowFeedback(false);
  };

  const handleFeedbackSkip = () => {
    setShowFeedback(false);
  };

  const handleStepError = (error, step = currentStep) => {
    const onboardingError = error instanceof OnboardingError 
      ? error 
      : new OnboardingError(error.message || 'An error occurred', ERROR_TYPES.UNKNOWN, ERROR_SEVERITY.MEDIUM, { step });
    
    errorHandler.handleError(onboardingError, { step, operation: 'step_error' });
    trackOnboardingError(onboardingError, { step, operation: 'step_error' });
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    
    try {
      setError(null);
      
      // Retry the appropriate operation based on current state
      if (!sessionData) {
        await initializeOnboarding();
      } else {
        await fetchOnboardingProgress();
      }
    } catch (retryError) {
      errorHandler.handleError(retryError, { 
        operation: 'retry',
        retry_attempt: retryCount + 1 
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRecoveryAction = async (action) => {
    try {
      switch (action) {
        case 'retry':
          await handleRetry();
          break;
        case 'reauthenticate':
          navigate('/sign-in?redirect=' + encodeURIComponent(window.location.pathname));
          break;
        case 'restart_onboarding':
          setSessionData(null);
          setOnboardingData({
            selectedPlan: null,
            phoneNumber: null,
            parrotConfig: null
          });
          setCurrentStep(1);
          setError(null);
          setRetryCount(0);
          await initializeOnboarding();
          break;
        case 'contact_support':
          window.open('mailto:support@parrotmate.com?subject=Onboarding Issue&body=I encountered an issue during onboarding. Please help.', '_blank');
          break;
        case 'check_connection':
          const isConnected = await checkNetworkConnectivity();
          if (isConnected) {
            await handleRetry();
          } else {
            setError(new OnboardingError(
              'Still unable to connect. Please check your internet connection.',
              ERROR_TYPES.NETWORK,
              ERROR_SEVERITY.HIGH,
              { retryable: true }
            ));
          }
          break;
        default:
          console.warn('Unknown recovery action:', action);
      }
    } catch (actionError) {
      errorHandler.handleError(actionError, { 
        operation: 'recovery_action',
        action 
      });
    }
  };

  const renderStepContent = () => {
    const step = ONBOARDING_STEPS[currentStep - 1];
    
    switch (step.component) {
      case 'plan':
        return (
          <PlanSelection
            onNext={handleNext}
            onBack={handleBack}
            data={onboardingData}
            setData={setOnboardingData}
            onComplete={(planData) => handleStepComplete('selectedPlan', planData)}
            onError={(error) => handleStepError(error, 1)}
            onRetry={() => handleRetry()}
            isOnboarding={true}
            step={1}
          />
        );
      
      case 'phone':
        return (
          <ModuleContextProvider>
            <PhoneNumberModule
              config={{
                context: 'onboarding',
                searchFilters: [
                  { type: 'country', enabled: true, required: false, defaultValue: 'US' },
                  { type: 'area-code', enabled: true, required: false },
                  { type: 'city', enabled: true, required: false },
                  { type: 'region', enabled: true, required: false },
                  { type: 'pattern', enabled: true, required: false }
                ],
                purchaseFlow: 'confirmation',
                showSkipOption: true,
                billingContext: {
                  source: 'onboarding',
                  category: 'phone-number-purchase',
                  step: 2
                },
                onPurchaseComplete: (phoneData) => {
                  console.log('Onboarding phone number result:', phoneData);
                  handleStepComplete('phoneNumber', phoneData);
                }
              }}
              onEvent={(event) => {
                console.log('Onboarding PhoneNumber event:', event);
                if (event.type === 'PURCHASE_FAILED' || event.type === 'SEARCH_FAILED') {
                  handleStepError(new Error(event.payload.error), 2);
                }
              }}
            />
          </ModuleContextProvider>
        );
      
      case 'parrot':
        return (
          <ParrotCreation
            onNext={handleNext}
            onBack={handleBack}
            data={onboardingData}
            setData={setOnboardingData}
            onComplete={(parrotData) => handleStepComplete('parrotConfig', parrotData)}
            onError={(error) => handleStepError(error, 3)}
            onRetry={() => handleRetry()}
            isOnboarding={true}
            step={3}
          />
        );
      
      default:
        return <div>Unknown step</div>;
    }
  };

  if (!isLoaded || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-gray-900">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="text-gray-600 mt-4 dark:text-gray-300 dark:text-gray-600">Setting up your onboarding...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-gray-900">
        <div className="max-w-2xl mx-auto px-4">
          <OnboardingErrorDisplay
            error={error}
            step={currentStep}
            retryCount={retryCount}
            isRetrying={isRetrying}
            onRetry={handleRetry}
            onRecoveryAction={handleRecoveryAction}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white shadow-sm dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Welcome to ParrotMate</h1>
              <p className="text-gray-600 mt-1 dark:text-gray-300 dark:text-gray-600">Let's get your AI assistant set up</p>
            </div>
            <div className="text-sm text-gray-500">
              Step {currentStep} of {ONBOARDING_STEPS.length}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Progress Indicator */}
      <div className="bg-white border-b dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <OnboardingProgressIndicator
            steps={ONBOARDING_STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
            showLabels={true}
            showDescription={true}
          />
          
          {/* Progress Bar for Mobile */}
          <div className="sm:hidden mt-4">
            <OnboardingProgressBar
              currentStep={currentStep}
              totalSteps={ONBOARDING_STEPS.length}
              showPercentage={true}
              animated={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 dark:bg-gray-800">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="large" />
              <span className="ml-3 text-gray-600 dark:text-gray-300 dark:text-gray-600">
                {currentStep === 1 ? 'Loading plans...' : 
                 currentStep === 2 ? 'Searching phone numbers...' : 
                 'Setting up your Parrot...'}
              </span>
            </div>
          )}
          
          {/* Step Content */}
          {!isLoading && renderStepContent()}
          
          {/* Quick Feedback */}
          {!isLoading && !error && completedSteps.includes(currentStep - 1) && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <QuickFeedback
                step={currentStep - 1}
                onFeedback={(feedback) => console.log('Quick feedback:', feedback)}
              />
            </div>
          )}
        </div>
        
        {/* Detailed Feedback Modal */}
        {showFeedback && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-md mx-4 max-h-[90vh] overflow-y-auto dark:bg-gray-800">
              <OnboardingFeedback
                step={currentStep - 1}
                onSubmit={handleFeedbackSubmit}
                onSkip={handleFeedbackSkip}
                showRating={true}
                showComments={true}
                showSuggestions={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="bg-white border-t dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300 dark:text-gray-600"
            >
              ← Back
            </button>
            <HelpTooltip content="Complete this step to continue to the next part of your setup">
              <button
                onClick={handleNext}
                disabled={!canProceed() || isLoading}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 dark:text-white"
              >
                {isLoading && <LoadingSpinner size="small" className="mr-2" />}
                {currentStep === ONBOARDING_STEPS.length ? 'Complete Setup' : 'Continue →'}
              </button>
            </HelpTooltip>
          </div>
        </div>
      </div>

      {/* Step Completion Celebration */}
      <StepCompletionCelebration
        stepName={ONBOARDING_STEPS[currentStep - 1]?.title}
        isVisible={showStepCelebration}
        onComplete={handleCelebrationComplete}
        showNextStep={currentStep < ONBOARDING_STEPS.length}
        nextStepName={ONBOARDING_STEPS[currentStep]?.title}
      />

      {/* Success Toast */}
      <OnboardingSuccessToast
        message={successToastMessage}
        isVisible={showSuccessToast}
        onHide={() => setShowSuccessToast(false)}
        position="top-right"
      />
    </div>
  );
}
